import { NODE_ENV_LOCAL_OR_DEVELOPMENT } from 'Constants/env';
import { SoftphoneLogger } from './index.types';


/**
 * Manual toggle for softphone logging
 *
 * This flag works in conjunction with NODE_ENV_LOCAL_OR_DEVELOPMENT.
 * Logs will ONLY be shown if BOTH conditions are true:
 * 1. Running in local/development environment (NODE_ENV_LOCAL_OR_DEVELOPMENT = true)
 * 2. This flag is set to true
 *
 * Set to true by default during development to facilitate debugging and understanding
 * the call flow. We maintain logs enabled throughout the entire development phase
 * of the new softphone to help identify issues quickly.
 *
 * Can be set to false even in development to reduce log verbosity in other PRs
 * or when working on unrelated features.
 *
 * In production, logs are ALWAYS disabled regardless of this flag.
 */
let softphoneLogsManuallyEnabled = false; // Temporarily enabled for audio settings debugging

/**
 * Check if logging should be enabled
 * Requires BOTH conditions to be true:
 * 1. Running in development environment (local/development)
 * 2. Manual flag is enabled
 *
 * @returns true only if in development AND manually enabled
 */
const shouldEnableLogging = (): boolean => {
  return NODE_ENV_LOCAL_OR_DEVELOPMENT && softphoneLogsManuallyEnabled;
};

/**
 * Enable or disable all softphone logging globally
 *
 * This only affects the manual toggle. Logs will still be disabled in production
 * regardless of this setting.
 *
 * @param enabled - true to enable logs (only works in development), false to disable
 */
export const setLoggingEnabled = (enabled: boolean): void => {
  softphoneLogsManuallyEnabled = enabled;
};

/**
 * Check if logging is currently enabled
 *
 * @returns true if logs are enabled (development environment AND manual flag is true)
 */
export const isLoggingEnabled = (): boolean => shouldEnableLogging();

/**
 * Creates a namespaced logger for softphone components
 *
 * @param namespace - The component name (e.g., 'SipService', 'useSoftphoneCalls')
 * @returns A logger with namespaced methods
 */
export const createLogger = (namespace: string): SoftphoneLogger => {
  const prefix = `[softphone:${namespace}]`;

  return {
    debug: (message: string, data?: object | Error) => {
      if (!shouldEnableLogging()) return;
      console.log(`${prefix} ${message}`, data ?? '');
    },
    info: (message: string, data?: object | Error) => {
      if (!shouldEnableLogging()) return;
      console.log(`${prefix} ${message}`, data ?? '');
    },
    warn: (message: string, data?: object | Error) => {
      if (!shouldEnableLogging()) return;
      console.warn(`${prefix} ${message}`, data ?? '');
    },
    error: (message: string, data?: object | Error) => {
      if (!shouldEnableLogging()) return;
      console.error(`${prefix} ${message}`, data ?? '');
    },
  };
};

// Adapters/Routers (bridge between legacy and new softphone)
export const callRouterLogger: SoftphoneLogger = createLogger('CallRouter');
export const audioSettingsLogger: SoftphoneLogger = createLogger(
  'AudioSettingsAdapter'
);

// Services
export const sipLogger: SoftphoneLogger = createLogger('SipService');
export const webrtcLogger: SoftphoneLogger = createLogger('WebRTCService');
export const audioLogger: SoftphoneLogger = createLogger('AudioService');
export const deviceLogger: SoftphoneLogger = createLogger('DeviceService');
export const lifecycleLogger: SoftphoneLogger =
  createLogger('ConnectionManager');
export const coordinatorLogger: SoftphoneLogger = createLogger(
  'SoftphoneCoordinator'
);
export const callManagerLogger: SoftphoneLogger = createLogger('CallManager');
export const recoveryLogger: SoftphoneLogger = createLogger(
  'RecoveryCoordinator'
);

// Hooks
export const callsLogger: SoftphoneLogger = createLogger('useSoftphoneCalls');
export const devicesHookLogger: SoftphoneLogger = createLogger(
  'useSoftphoneDevices'
);
export const connectionHookLogger: SoftphoneLogger = createLogger(
  'useConnectionStatus'
);
export const callControlsLogger: SoftphoneLogger = createLogger(
  'useSoftphoneCallControls'
);
export const keyboardShortcutsLogger: SoftphoneLogger = createLogger(
  'useSoftphoneKeyboardShortcuts'
);

// Provider
export const providerLogger: SoftphoneLogger = createLogger('Provider');

// Components
export const softphonePanelLogger: SoftphoneLogger =
  createLogger('SoftphonePanel');
export const softphoneCallControlsLogger: SoftphoneLogger = createLogger(
  'SoftphoneCallControls'
);
